import { useCallback, useEffect, useState } from "react";
import { Button, Form, FormLabel, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../../api"
import { ErrorDialog } from "../../../components/ErrorDialog";
import { ImageBase64Converter } from "../../../components/ImageBase64Converter";
import { ImageUploader } from "../../../components/ImageUploader";
import { ProfileAvatarUploader } from "../../../components/ProfileAvatarUploader";
import { SuccessDialog } from "../../../components/SuccessDialog";
import { ICargo, ICreatorProfile, IProfile, IReferral } from "../../../interfaces";
import { Utils } from "../../../utils";
import { TextFormGroup } from "./components/TextFormGroup";

export const NftProfilePage = () => {
  const projectCode = 'NFT'
  const api = useApi(projectCode)
  const { t } = useTranslation()
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [profile, setProfile] = useState<IProfile>();
  const [mail, setMail] = useState<string>();
  const [introduction, setIntroduction] = useState<string>('');
  const [youtubeUrl, setYoutubeUrl] = useState<string>('');
  const [idNo, setIdNo] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [kycImg, setKycImg] = useState<string>('');
  const [notifyLinked, setNotifyLinked] = useState<boolean>();
  const [bindingProcessing, setBindingProcessing] = useState<boolean>(false);

  const fetchReferral = useCallback(() => {
    api.getReferralCode((cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        const referral: IReferral = cargo.info;
        api.getCreatorProfile(referral.simpleCode, (cargo: ICargo) => {
          if (cargo.returnCode === '000000') {
            const creatorProfile: ICreatorProfile = cargo.info;
            if (creatorProfile.introduction) {
              setIntroduction(creatorProfile.introduction);
            }
            if (creatorProfile.youtubeUrl) {
              setYoutubeUrl(creatorProfile.youtubeUrl);
            }
          }
        })
      }
    })
  }, [api])

  const fetchProfile = useCallback(() => {
    api.getProfile((cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        setProfile(cargo.info);
      }
    })
  }, [api])

  const fetchNotifyStatus = useCallback(() => {
    api.getLineNotifyStatus((cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        setNotifyLinked(cargo.info);
      }
    })
  }, [api])

  useEffect(() => {
    if (mounted) return;
    setMounted(true);

    fetchReferral()
    fetchProfile()
    fetchNotifyStatus()
  }, [fetchProfile, fetchReferral, fetchNotifyStatus, mounted])

  const bindNotify = () => {
    setBindingProcessing(true);
    const url = new URL('https://notify-bot.line.me/oauth/authorize');
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', 'G71embnViTWtvSyPQqup27');
    url.searchParams.set('redirect_uri', window.location.href);
    url.searchParams.set('state', Utils().uuid());
    url.searchParams.set('scope', 'notify');

    window.location.href = url.toString();
  }

  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");
    if (!projectCode || !code || !state) return;

    if (bindingProcessing) return;
    setBindingProcessing(true);

    if (url.searchParams.has("code")) {
      api.lineNotifyBinding(code, window.location.href.split('?')[0], (cargo: ICargo) => {
        setBindingProcessing(false)
        if (cargo.returnCode === '000000') {
          SuccessDialog({ title: "綁定成功" })
        } else {
          ErrorDialog({ title: "綁定失敗" })
        }
        navigate('/web/profile')
      })
    }
  }, [api, bindingProcessing, navigate, projectCode])

  const submitKycApplication = () => {
    api.submitKyc(kycImg, idNo, name, (cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        SuccessDialog({ title: "上傳成功", content: "等待審核中" })
      }
    })
  }

  return (
    <>
      <div className="p-4"></div>
      <div className="px-3">
        {
          profile?.kycStatus === 1 && <div className="px-3 my-3 px-sm-4 pb-5 mx-auto w-100" style={{ maxWidth: '500px', border: '1px solid #9b00ff', borderRadius: '12px' }}>
            <div className="pt-3 txt-18 text-center">實名認證審核中</div>
          </div>
        }
        {
          profile?.kycStatus !== 1 && profile?.kycStatus !== 2 && <div className="px-3 px-sm-4 pb-5 mx-auto w-100" style={{ maxWidth: '500px', border: '1px solid #9b00ff', borderRadius: '12px' }}>
            <div className="pt-3 txt-18 text-center">實名認證</div>
            <hr />
            <FormLabel>身份證字號</FormLabel>
            <InputGroup>
              <Form.Control
                type="text"
                value={idNo}
                onChange={(e) => {
                  e.preventDefault();
                  setIdNo(e.target.value)
                }}
              />
            </InputGroup>
            <FormLabel>姓名</FormLabel>
            <InputGroup>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => {
                  e.preventDefault();
                  setName(e.target.value)
                }}
              />
            </InputGroup>
            <div className="mt-5">請上傳證件</div>
            <div className="p-3 w-50">
              <ImageBase64Converter onConvert={(img) => {
                setKycImg(img);
              }} />
            </div>
            {idNo && name && kycImg 
              ? <Button onClick={submitKycApplication}>送出審核</Button>
              : <Button disabled>送出審核</Button>
            }
          </div>
        }
        <div className="mt-5 px-3 px-sm-4 pb-5 mx-auto w-100" style={{ maxWidth: '500px', border: '1px solid #9b00ff', borderRadius: '12px' }}>
          <div className="pt-3 txt-18 text-center">帳戶設定</div>
          <hr />

          {notifyLinked ? <div>LINE Notify 已綁定</div> : <div>LINE Notify 未綁定</div>}
          {notifyLinked && <Button onClick={bindNotify} disabled={bindingProcessing}>重新綁定LINE通知</Button>}
          {!notifyLinked && <Button onClick={bindNotify} disabled={bindingProcessing}>綁定LINE通知</Button>}

          <div className="txt-14 p-3 pt-5">
            <div>頭像圖片</div>
            <div className="m-3" style={{ width: '200px' }}>
              <ProfileAvatarUploader
                image={Utils().normalizeImagePath(profile?.avator || '', projectCode)}
                projectCode={projectCode}
                onUploaded={(cargo: ICargo) => {
                  if (cargo.returnCode === '000000') {
                    api.updateAvator(cargo.info['file'])
                  }
                }}
              />
            </div>
            <div>檔案格式：JPG, JPEG, PNG, GIF</div>
            <div>建議尺寸：200 x 200</div>
            <div>檔案限制：小於 1MB</div>
          </div>

          <div className="txt-14 p-3 pt-5">
            <div>橫幅圖片</div>
            <div className="m-3" style={{ width: '200px' }}>
              <ImageUploader projectCode={projectCode} onUploaded={(cargo: ICargo) => {
                if (cargo.returnCode === '000000') {
                  api.updateCreatorBanner(cargo.info['file'])
                }
              }}></ImageUploader>
            </div>
            <div>檔案格式：JPG, JPEG, PNG, GIF</div>
            <div>建議尺寸：1920 x 420</div>
            <div>檔案限制：小於 1MB</div>
          </div>
          <Form.Group className="mt-4">
            <FormLabel>Email</FormLabel>
            <InputGroup>
              {profile?.email && <Form.Control
                type="text"
                value={profile?.email}
                disabled={!!profile?.email}
              />}
              {!profile?.email && <Form.Control
                type="text"
                value={mail}
                onChange={(e) => {
                  e.preventDefault();
                  setMail(e.target.value)
                }}
              />}
              <InputGroup.Text className="cursor-pointer px-3" style={{ borderRadius: '0 12px 12px 0' }} onClick={() => {
                if (!mail) return;
                api.updateMail(mail, (cargo: ICargo) => {
                  if (cargo.returnCode === '000000') {
                    SuccessDialog({ title: "完成" })
                  }
                })
              }} >變更</InputGroup.Text>
            </InputGroup>
          </Form.Group>
          <TextFormGroup title={t('nickname')} initValue={profile?.nickname || ''} onSave={((updatedValue) => {
            api.updateNickname(updatedValue, (cargo: ICargo) => {
              if (cargo.returnCode === '000000') {
                SuccessDialog({ title: "完成" })
              }
            })
          })} />
          <Form.Group className="mt-4">
            <FormLabel>{t('introduction')}</FormLabel>
            <InputGroup>
              <Form.Control
                as="textarea"
                value={introduction}
                onChange={(e) => {
                  e.preventDefault();
                  setIntroduction(e.target.value)
                }}
              />
              <InputGroup.Text className="cursor-pointer px-3" style={{ borderRadius: '0 12px 12px 0' }} onClick={() => {
                if (!introduction) return;
                api.updateCreatorIntroduction(introduction, (cargo: ICargo) => {
                  if (cargo.returnCode === '000000') {
                    SuccessDialog({ title: "完成" })
                  }
                })
              }} >變更</InputGroup.Text>
            </InputGroup>
          </Form.Group>
          <TextFormGroup title="Youtube" initValue={youtubeUrl} onSave={((updatedValue) => {
            api.updateYoutubeUrl(updatedValue, (cargo: ICargo) => {
              if (cargo.returnCode === '000000') {
                SuccessDialog({ title: "完成" })
              }
            })
          })} />
        </div>
      </div>
      <div className="p-5"></div>
    </>
  )
}